/**
 * Donation Service
 * 
 * Handles donation submissions and management
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import { 
  emailService,
  sendDonationNotification,
  sendDonationThankYou
} from './emailService';
import type { 
  DonationFormData, 
  Donation, 
  DonationStatus,
  PaymentStatus,
  GetDonationsOptions 
} from '@/types/donation';

/**
 * Calculate donor level based on donation amount
 */
const calculateDonorLevel = (amount: number): string | null => {
  if (amount >= 1000) return 'Legacy Patron';
  if (amount >= 500) return 'Guardian';
  if (amount >= 250) return 'Community Leader';
  if (amount >= 100) return 'Community Champion';
  if (amount >= 25) return 'Supporter';
  return null;
};

/**
 * Submit donation form
 */
export const submitDonation = async (
  data: DonationFormData
): Promise<ServiceResponse<Donation>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Validate required fields
    if (!data.firstName || !data.lastName || !data.email || !data.donationAmount || !data.donationType) {
      return errorResponse(handleServiceError(
        new Error('All required fields must be filled'),
        'donationService.submitDonation',
        { data }
      ));
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      return errorResponse(handleServiceError(
        new Error('Invalid email address'),
        'donationService.submitDonation',
        { data }
      ));
    }

    // Validate donation amount
    if (data.donationAmount <= 0) {
      return errorResponse(handleServiceError(
        new Error('Donation amount must be greater than 0'),
        'donationService.submitDonation',
        { data }
      ));
    }

    // Calculate donor level if not provided
    const donorLevel = data.donorLevel || calculateDonorLevel(data.donationAmount);

    // Insert donation
    const { data: donation, error: insertError } = await safeFrom('donations')
      .insert([{
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.trim().toLowerCase(),
        phone: data.phone?.trim() || null,
        address: data.address?.trim() || null,
        donation_amount: data.donationAmount,
        donation_type: data.donationType,
        donor_level: donorLevel,
        message: data.message?.trim() || null,
        anonymous: data.anonymous || false,
        payment_status: 'pending' as PaymentStatus,
        status: 'new' as DonationStatus,
      }])
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    const created = donation as Donation;

    // Send notification email to admin (non-blocking)
    const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'admin@example.com';
    sendDonationNotification({
      firstName: created.first_name,
      lastName: created.last_name,
      email: created.email,
      amount: created.donation_amount,
      type: created.donation_type,
      donorLevel: created.donor_level || undefined,
      anonymous: created.anonymous,
    }, adminEmail).catch(error => {
      console.error('[donationService] Failed to send notification email:', error);
    });

    // Send thank you email to donor (non-blocking)
    const organizationName = import.meta.env.VITE_ORG_NAME || 'Our Organization';
    sendDonationThankYou({
      firstName: created.first_name,
      email: created.email,
      amount: created.donation_amount,
      type: created.donation_type,
      donorLevel: created.donor_level || undefined,
    }, organizationName).catch(error => {
      console.error('[donationService] Failed to send thank you email:', error);
    });

    // Audit log
    await auditContent.create('donation', created.id.toString(), {
      email: created.email,
      amount: created.donation_amount,
      type: created.donation_type,
    });

    return successResponse(created);
  } catch (error) {
    console.error('[donationService] Error submitting donation:', error);
    return errorResponse(handleServiceError(error, 'donationService.submitDonation', { data }));
  }
};

/**
 * Get donations (admin only)
 */
export const getDonations = async (
  options: GetDonationsOptions = {}
): Promise<ServiceResponse<Donation[]>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Verify admin access
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    let query = safeFrom('donations')
      .select('*');

    // Apply filters
    if (options.status) {
      query = query.eq('status', options.status) as any;
    }
    if (options.payment_status) {
      query = query.eq('payment_status', options.payment_status) as any;
    }

    // Apply ordering
    const orderBy = options.orderBy || 'created_at';
    const orderDirection = options.orderDirection || 'desc';
    query = query.order(orderBy, { ascending: orderDirection === 'asc' }) as any;

    // Apply pagination
    if (options.limit) {
      query = query.limit(options.limit) as any;
    }
    if (options.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 50) - 1) as any;
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return successResponse((data || []) as Donation[]);
  } catch (error) {
    console.error('[donationService] Error fetching donations:', error);
    return errorResponse(handleServiceError(error, 'donationService.getDonations', { options }));
  }
};

/**
 * Update donation status (admin only)
 */
export const updateDonationStatus = async (
  id: number,
  status: DonationStatus
): Promise<ServiceResponse<Donation>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: donation, error: updateError } = await safeFrom('donations')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    const updated = donation as Donation;

    // Audit log
    await auditContent.update('donation', id.toString(), {}, {
      status: updated.status,
    });

    return successResponse(updated);
  } catch (error) {
    console.error('[donationService] Error updating donation status:', error);
    return errorResponse(handleServiceError(error, 'donationService.updateDonationStatus', { id, status }));
  }
};

/**
 * Update payment status (admin only)
 */
export const updatePaymentStatus = async (
  id: number,
  payment_status: PaymentStatus
): Promise<ServiceResponse<Donation>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: donation, error: updateError } = await safeFrom('donations')
      .update({ payment_status })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    const updated = donation as Donation;

    // Audit log
    await auditContent.update('donation', id.toString(), {}, {
      payment_status: updated.payment_status,
    });

    return successResponse(updated);
  } catch (error) {
    console.error('[donationService] Error updating payment status:', error);
    return errorResponse(handleServiceError(error, 'donationService.updatePaymentStatus', { id, payment_status }));
  }
};

/**
 * Get donation by ID (admin only)
 */
export const getDonationById = async (
  id: number
): Promise<ServiceResponse<Donation | null>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { data, error } = await safeFrom('donations')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return successResponse(data as Donation | null);
  } catch (error) {
    console.error('[donationService] Error fetching donation:', error);
    return errorResponse(handleServiceError(error, 'donationService.getDonationById', { id }));
  }
};

/**
 * Get donation statistics (admin only)
 */
export const getDonationStats = async (): Promise<ServiceResponse<{
  totalDonations: number;
  totalAmount: number;
  averageAmount: number;
  byStatus: Record<string, number>;
  byPaymentStatus: Record<string, number>;
}>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { user } = await safeAuth.getUser();
    if (user?.error) {
      throw user.error;
    }

    const { data: donations, error } = await safeFrom('donations')
      .select('donation_amount, status, payment_status');

    if (error) {
      throw error;
    }

    const donationsList = (donations || []) as Array<{ donation_amount: number; status: string; payment_status: string }>;
    
    const totalDonations = donationsList.length;
    const totalAmount = donationsList.reduce((sum, d) => sum + Number(d.donation_amount || 0), 0);
    const averageAmount = totalDonations > 0 ? totalAmount / totalDonations : 0;

    const byStatus: Record<string, number> = {};
    const byPaymentStatus: Record<string, number> = {};

    donationsList.forEach(d => {
      byStatus[d.status] = (byStatus[d.status] || 0) + 1;
      byPaymentStatus[d.payment_status] = (byPaymentStatus[d.payment_status] || 0) + 1;
    });

    return successResponse({
      totalDonations,
      totalAmount,
      averageAmount,
      byStatus,
      byPaymentStatus,
    });
  } catch (error) {
    console.error('[donationService] Error fetching donation stats:', error);
    return errorResponse(handleServiceError(error, 'donationService.getDonationStats'));
  }
};

export const donationService = {
  submitDonation,
  getDonations,
  updateDonationStatus,
  updatePaymentStatus,
  getDonationById,
  getDonationStats,
};
